/**
 * Form.io sanitizes HTML before it looks up a translation, which turns every
 * `<br/>` / `<br />` into `<br>`. Keys must use the sanitized form to match.
 * Nothing else is rewritten so keys that already work keep working.
 *
 * @param {string} key
 *
 * @returns {string}
 */
export const normalizeKey = key => (typeof key === 'string'
  ? key.replace(/<br\s*\/?>/gi, '<br>')
  : key)

/**
 * Normalize every key of a flat `{ key: value }` map. When two keys collapse
 * into the same normalized key the non-empty value wins.
 *
 * @param {Object<string, string>} map
 *
 * @returns {Object<string, string>}
 */
export const normalizeMap = (map = {}) => Object.entries(map || {}).reduce((acc, [key, value]) => {
  const normalized = normalizeKey(key)
  const current = acc[normalized]

  if (current === undefined || current === '' || current === null) {
    acc[normalized] = value
  }

  return acc
}, {})

/**
 * Normalize the keys of every language: `{ lang: { key: value } }`.
 *
 * @param {Object<string, Object<string, string>>} translations
 *
 * @returns {Object<string, Object<string, string>>}
 */
export const normalizeTranslations = (translations = {}) => Object.keys(translations || {}).reduce((acc, language) => {
  acc[language] = normalizeMap(translations[language])

  return acc
}, {})

/**
 * Remove empty values. i18next returns an empty string as-is, which would
 * render an empty label instead of falling back to the source text.
 *
 * @param {Object<string, Object<string, string>>} translations
 *
 * @returns {Object<string, Object<string, string>>}
 */
export const stripEmptyValues = (translations = {}) => Object.keys(translations || {}).reduce((acc, language) => {
  acc[language] = Object.entries(translations[language] || {}).reduce((map, [key, value]) => {
    if (typeof value === 'string' ? value.trim() !== '' : value !== undefined && value !== null) {
      map[key] = value
    }

    return map
  }, {})

  return acc
}, {})
